export interface ChainItem {
  id: string;
  draftIndex?: number;
  dependsOn?: number[];
}

interface Maps {
  /** id -> set of predecessor ids */
  preds: Map<string, Set<string>>;
  /** id -> set of dependent ids */
  deps: Map<string, Set<string>>;
}

export const buildDependencyMaps = (items: ChainItem[]): Maps => {
  const idxToId = new Map<number, string>();
  for (const it of items) {
    if (it.draftIndex !== undefined) idxToId.set(it.draftIndex, it.id);
  }
  const preds = new Map<string, Set<string>>();
  const deps = new Map<string, Set<string>>();
  for (const it of items) {
    preds.set(it.id, new Set());
    deps.set(it.id, new Set());
  }
  for (const it of items) {
    for (const p of it.dependsOn ?? []) {
      const predId = idxToId.get(p);
      if (!predId) continue; // missing predecessor — silently skip (defensive)
      preds.get(it.id)!.add(predId);
      deps.get(predId)!.add(it.id);
    }
  }
  return { preds, deps };
};

const closure = (seed: string, edges: Map<string, Set<string>>): Set<string> => {
  const visited = new Set<string>([seed]);
  const stack = [seed];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const next of edges.get(cur) ?? []) {
      if (!visited.has(next)) {
        visited.add(next);
        stack.push(next);
      }
    }
  }
  return visited;
};

export const selectWithCascade = (
  current: Set<string>,
  toggled: string,
  items: ChainItem[],
): Set<string> => {
  const { preds } = buildDependencyMaps(items);
  const next = new Set(current);
  for (const id of closure(toggled, preds)) next.add(id);
  return next;
};

export const deselectWithCascade = (
  current: Set<string>,
  toggled: string,
  items: ChainItem[],
): Set<string> => {
  const { deps } = buildDependencyMaps(items);
  const next = new Set(current);
  for (const id of closure(toggled, deps)) next.delete(id);
  return next;
};
